import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import CustomFormModal from '@/components/community/CustomFormModal';
import type { CustomFormField } from '@/services/communityService';
import showToast from '@/components/ui/Toast';

interface CustomFormSectionProps {
  fields: CustomFormField[];
  onChange: (fields: CustomFormField[]) => void;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  sportCategorySlug?: string | null;
}

export const CustomFormSection: React.FC<CustomFormSectionProps> = ({
  fields,
  onChange,
  enabled,
  onToggleEnabled,
  sportCategorySlug = null,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomFormField | null>(null);

  const handleOpenAdd = () => {
    if (fields.length >= 5) {
      showToast.error('Maksimal 5 pertanyaan formulir tambahan');
      return;
    }
    setEditingField(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (field: CustomFormField) => {
    setEditingField(field);
    setModalOpen(true);
  };

  const handleDeleteField = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(fields.filter((f) => f.id !== id));
  };

  const handleSaveField = (savedField: CustomFormField) => {
    if (editingField) {
      // Update existing
      onChange(fields.map((f) => (f.id === savedField.id ? savedField : f)));
    } else {
      // Add new
      onChange([...fields, savedField]);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'number':
        return 'ph:hash-bold';
      case 'option':
        return 'ph:list-bullets-bold';
      case 'date':
        return 'ph:calendar-blank-bold';
      case 'text':
      default:
        return 'ph:text-t-bold';
    }
  };

  const getTypeLabel = (field: CustomFormField) => {
    if (field.type === 'option') {
      const count = field.options?.length || 0;
      return `${count} pilihan`;
    }
    switch (field.type) {
      case 'number':
        return 'Angka';
      case 'date':
        return 'Tanggal';
      case 'text':
      default:
        return 'Teks singkat';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-4 shadow-xs">
      {/* Header with Switch */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Formulir Tambahan</h3>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => {
            const next = !enabled;
            onToggleEnabled(next);
          }}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
            enabled ? 'bg-[#FC480D]' : 'bg-gray-200'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Description */}
      <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-normal">
        Pertanyaan ekstra untuk pendaftar, misal posisi bermain atau ukuran jersey. Nama, email, dan nomor WhatsApp sudah terisi otomatis dari akun Sporto pemain.
      </p>

      {/* When Enabled */}
      {enabled && (
        <div className="space-y-3 pt-1">
          {/* List of Questions */}
          {fields.length > 0 && (
            <div className="space-y-2.5">
              {fields.map((field) => (
                <div
                  key={field.id}
                  onClick={() => handleOpenEdit(field)}
                  className="p-3 sm:p-3.5 rounded-xl border border-gray-200/90 bg-white hover:border-gray-300 hover:shadow-2xs transition-all flex items-center justify-between gap-3 cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                      <Icon icon={getTypeIcon(field.type)} className="w-4 h-4" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-sm text-gray-900 truncate">
                          {field.title}
                        </span>
                        {(field.template_type === 'form_role' || field.template_type === 'role') && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200 shrink-0">
                            <Icon icon="ph:lightning-bold" className="w-2.5 h-2.5 text-amber-500" />
                            Posisi Pemain
                          </span>
                        )}
                        {field.template_type === 'team' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200 shrink-0">
                            <Icon icon="ph:lightning-bold" className="w-2.5 h-2.5 text-blue-500" />
                            Tim
                          </span>
                        )}
                        {field.template_type === 'player_number' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200 shrink-0">
                            <Icon icon="ph:lightning-bold" className="w-2.5 h-2.5 text-emerald-500" />
                            Nomor Punggung
                          </span>
                        )}
                        {field.template_type === 'level' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-orange-50 text-[#FC480D] rounded border border-orange-200 shrink-0">
                            <Icon icon="ph:lightning-bold" className="w-2.5 h-2.5 text-[#FC480D]" />
                            Level
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <span>{getTypeLabel(field)}</span>
                        <span>•</span>
                        <span className={field.required ? 'text-orange-600 font-semibold' : 'text-gray-400 font-normal'}>
                          {field.required ? 'Wajib' : 'Opsional'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(field);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      title="Edit pertanyaan"
                    >
                      <Icon icon="ph:pencil-simple-line-bold" className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteField(field.id, e)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="Hapus pertanyaan"
                    >
                      <Icon icon="ph:trash-bold" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Question Button */}
          {fields.length < 5 ? (
            <button
              type="button"
              onClick={handleOpenAdd}
              className="w-full py-2.5 px-4 rounded-xl border border-dashed border-gray-300 text-xs sm:text-sm font-bold text-gray-700 hover:text-[#FC480D] hover:border-[#FC480D] hover:bg-orange-50/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-4 h-4 text-[#FC480D]" />
              <span>Tambah Pertanyaan</span>
              <span className="text-xs text-gray-400 font-normal">
                ({fields.length}/5)
              </span>
            </button>
          ) : (
            <div className="p-2.5 bg-gray-50 rounded-xl text-center text-xs text-gray-500 font-medium">
              Maksimal 5 pertanyaan formulir telah tercapai
            </div>
          )}
        </div>
      )}

      {/* Modal Tambah / Edit */}
      <CustomFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingField(null);
        }}
        onSave={handleSaveField}
        initialField={editingField}
        existingFields={fields}
        sportCategorySlug={sportCategorySlug}
      />
    </div>
  );
};

export default CustomFormSection;
